/**
 * Landing page Afterpay redirects the buyer to after approval.
 * Reads `orderToken`, `status` and `kr_order_id` from the URL,
 * calls POST /api/shop/afterpay/confirm, then routes to the receipt.
 */
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { shopPaymentsAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

export default function AfterpayReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('confirming'); // confirming | paid | cancelled | error
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    const kr_order_id = params.get('kr_order_id');
    const order_token = params.get('orderToken') || params.get('token');
    const status = params.get('status') || 'SUCCESS';
    if (!kr_order_id || !order_token) {
      setState('error');
      setErrMsg('Missing Afterpay parameters — please try again.');
      return;
    }
    (async () => {
      try {
        const r = await shopPaymentsAPI.afterpayConfirm({
          kr_order_id, order_token, status,
        });
        if (r.data.payment_status === 'paid') setState('paid');
        else if (r.data.payment_status === 'cancelled') setState('cancelled');
        else setState('error');
      } catch (e) {
        setState('error');
        setErrMsg(getErrorMessage(e, 'Confirmation failed'));
      }
    })();
  }, [params]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="afterpay-return-page">
      <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 p-8 text-center space-y-3 shadow-sm">
        {state === 'confirming' && (<>
          <Loader2 size={32} className="mx-auto animate-spin text-slate-400" />
          <p className="text-sm text-slate-500">Confirming your Afterpay payment…</p>
        </>)}
        {state === 'paid' && (<>
          <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
          <h1 className="text-lg font-semibold">Payment received</h1>
          <p className="text-sm text-slate-500">Your Afterpay installment plan is set up.</p>
          <Link to="/shop/orders" className="inline-block mt-2 text-sm underline">View my orders</Link>
        </>)}
        {state === 'cancelled' && (<>
          <XCircle size={40} className="mx-auto text-amber-500" />
          <h1 className="text-lg font-semibold">Payment cancelled</h1>
          <button onClick={() => navigate('/checkout/cart')} className="text-sm underline">Back to checkout</button>
        </>)}
        {state === 'error' && (<>
          <XCircle size={40} className="mx-auto text-rose-500" />
          <h1 className="text-lg font-semibold">Something went wrong</h1>
          {errMsg && <p className="text-xs text-slate-500">{errMsg}</p>}
          <button onClick={() => navigate('/checkout/cart')} className="text-sm underline">Back to checkout</button>
        </>)}
      </div>
    </div>
  );
}
